import React, { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  Boxes,
  ClipboardList,
  Clock3,
  Crown,
  IndianRupee,
  Package,
  PackageCheck,
  RefreshCcw,
  ShoppingCart,
  Sparkles,
  TrendingDown,
  TrendingUp,
  Truck,
  Activity,
  ChevronRight,
  ShieldAlert,
  Warehouse,
  Power,
  X,
  Eye,
  CalendarDays,
  ChartNoAxesColumnIncreasing,
  Filter,
  Layers3,
  MapPin,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  getAuthSession,
  getAuthUser,
  hasPermission,
} from "../../AdminAuth/components/authStore";
import { warehouseService } from "../../../services/warehouse.service";
import { warehouseInventoryService } from "../../../services/warehouseInventory.service";
import ConfirmModal from "../../Dashboard/components/ConfirmModal";
import { getWarehouseRuntimeState } from "../../../utils/warehouseRuntime";
import warehouseDashboardHero from "../../../assets/warehouse-dashboard-hero.png";

const BORDER = "#f2c6cc";

function formatNumber(value) {
  const n = Number(value || 0);
  return Number.isFinite(n) ? n.toLocaleString("en-IN") : "0";
}

function formatCurrency(value) {
  const n = Number(value || 0);
  return `₹${n.toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;
}

function getErrorMessage(err) {
  return err?.message || "Something went wrong";
}

function toDateInput(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

function formatShortDate(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
  });
}

function getRange(rangeKey) {
  if (rangeKey === "all") return { range: "all" };

  const today = new Date();
  const end = toDateInput(today);
  const startDate = new Date(today);

  if (rangeKey === "7d") startDate.setDate(today.getDate() - 6);
  else if (rangeKey === "30d") startDate.setDate(today.getDate() - 29);
  else if (rangeKey === "90d") startDate.setDate(today.getDate() - 89);
  else {
    startDate.setDate(1);
  }

  return {
    range: rangeKey === "month" ? "month" : rangeKey,
    fromDate: toDateInput(startDate),
    toDate: end,
  };
}

function Card({ children, className = "" }) {
  return (
    <div
      className={`overflow-hidden rounded-[26px] border bg-white  ${className}`}
      style={{ borderColor: BORDER }}
    >
      {children}
    </div>
  );
}

function SectionHeader({ title, subtitle, right }) {
  return (
    <div className="flex flex-col gap-3 border-b border-[#f7dde1] px-5 py-4 md:flex-row md:items-center md:justify-between">
      <div>
        <div className="text-[19px] font-bold text-slate-900">{title}</div>
        {subtitle ? (
          <div className="mt-1 text-[13px] text-slate-500">{subtitle}</div>
        ) : null}
      </div>
      {right ? <div>{right}</div> : null}
    </div>
  );
}

function StatCard({
  icon: Icon,
  title,
  value,
  note,
  chip,
  glow = "from-[#fff7f8] via-[#ffffff] to-[#fff1f3]",
  iconBg = "bg-[#fff1f3]",
  iconText = "text-[#d86a78]",
}) {
  return (
    <div
      className={`rounded-[24px] border border-[#f2d7dc] bg-gradient-to-br ${glow} p-5 `}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <div className="text-[12px] font-semibold uppercase tracking-[0.18em] text-slate-400">
              {title}
            </div>
            {chip ? (
              <span className="rounded-full bg-white/90 px-2.5 py-1 text-[10px] font-semibold text-[#d86a78] shadow-sm">
                {chip}
              </span>
            ) : null}
          </div>
          <div className="mt-3 truncate text-[28px] font-bold text-slate-900">
            {value}
          </div>
          <div className="mt-1 text-[13px] text-slate-500">{note}</div>
        </div>

        <div
          className={`grid h-12 w-12 shrink-0 place-items-center rounded-[16px] ${iconBg} ${iconText}`}
        >
          <Icon size={22} />
        </div>
      </div>
    </div>
  );
}

function InsightChip({
  icon: Icon,
  label,
  value,
  tone = "rose",
}) {
  const tones = {
    rose: {
      card: "from-white via-[#fff9fa] to-[#fff0f3]",
      icon: "bg-[#fff0f4] text-[#fb4773]",
      wave: "bg-[#ffdce5]",
    },
    mint: {
      card: "from-white via-[#f8fffc] to-[#eafff7]",
      icon: "bg-[#ebfbf5] text-[#08a875]",
      wave: "bg-[#c9f3e5]",
    },
    blue: {
      card: "from-white via-[#f8fbff] to-[#eef7ff]",
      icon: "bg-[#edf7ff] text-[#258be8]",
      wave: "bg-[#d5eaff]",
    },
    amber: {
      card: "from-white via-[#fffdf7] to-[#fff7df]",
      icon: "bg-[#fff7df] text-[#e99a00]",
      wave: "bg-[#ffe7a8]",
    },
  };
  const selectedTone = tones[tone] || tones.rose;

  return (
    <div
      className={`relative flex min-h-[88px] min-w-0 items-center gap-3 overflow-hidden rounded-[18px] border border-white/90 bg-gradient-to-br ${selectedTone.card} px-3 py-3 shadow-[0_8px_20px_rgba(121,62,77,0.08)]`}
    >
      <div
        className={`relative z-10 grid h-11 w-11 shrink-0 place-items-center rounded-full ${selectedTone.icon}`}
      >
        <Icon size={21} strokeWidth={2.15} />
      </div>
      <div className="relative z-10 min-w-0">
        <div className="truncate text-[11px] font-semibold text-slate-500">{label}</div>
        <div className="mt-1 truncate text-[15px] font-extrabold text-slate-950">{value}</div>
      </div>
      <div
        className={`absolute -bottom-9 -left-3 h-14 w-[120%] rotate-[-4deg] rounded-[45%] opacity-65 ${selectedTone.wave}`}
      />
    </div>
  );
}

function ProductModal({ open, title, subtitle, items = [], tone = "top", onClose }) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[1200] flex items-center justify-center bg-slate-900/55 px-4 py-6 backdrop-blur-[2px]">
      <div className="max-h-[88vh] w-full max-w-3xl overflow-hidden rounded-[28px] border border-[#f2d7dc] bg-white shadow-[0_30px_90px_rgba(216,106,120,0.18)]">
        <div className="flex items-start justify-between gap-4 border-b border-[#f7dde1] px-6 py-5">
          <div>
            <div className="text-[22px] font-bold text-slate-900">{title}</div>
            <div className="mt-1 text-[13px] text-slate-500">{subtitle}</div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="grid h-10 w-10 place-items-center rounded-full bg-[#fff1f3] text-[#d86a78] transition hover:bg-[#ffe6ea] hover:text-[#c95b69]"
          >
            <X size={18} />
          </button>
        </div>

        <div className="max-h-[calc(88vh-90px)] overflow-y-auto px-6 py-5">
          {items.length ? (
            <div className="space-y-3">
              {items.map((item, idx) => (
                <div
                  key={`${item?.productId || item?.sku || idx}`}
                  className="flex items-center justify-between gap-4 rounded-[20px] border border-[#f2d7dc] bg-[linear-gradient(180deg,#fffafb_0%,#ffffff_100%)] px-4 py-4"
                >
                  <div className="flex min-w-0 items-center gap-4">
                    <div className="grid h-12 w-12 shrink-0 place-items-center rounded-[16px] bg-[#fff1f3] text-[#d86a78]">
                      {idx === 0 ? (
                        <Crown size={18} />
                      ) : tone === "top" ? (
                        <TrendingUp size={18} />
                      ) : (
                        <TrendingDown size={18} />
                      )}
                    </div>

                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="truncate text-[15px] font-bold text-slate-900">
                          {item?.productName || "Unnamed product"}
                        </div>
                        <span className="rounded-full bg-[#fff1f3] px-2 py-0.5 text-[10px] font-semibold text-[#d86a78]">
                          Rank #{idx + 1}
                        </span>
                      </div>
                      <div className="mt-1 text-[12px] text-slate-500">
                        SKU: {item?.sku || "-"}
                      </div>
                    </div>
                  </div>

                  <div className="text-right">
                    <div className="text-[16px] font-bold text-slate-900">
                      {formatNumber(item?.totalQty)}
                    </div>
                    <div className="text-[12px] text-slate-500">Units sold</div>
                    <div className="mt-1 text-[13px] font-semibold text-slate-700">
                      {formatCurrency(item?.totalSales)}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-16 text-center">
              <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-[#fff1f3] text-[#d86a78]">
                <Layers3 size={24} />
              </div>
              <div className="mt-4 text-[16px] font-semibold text-slate-800">
                No products found
              </div>
              <div className="mt-1 text-[13px] text-slate-500">
                There is no product data available for the selected filters.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TrendChart({ items = [], period = "day" }) {
  const scrollRef = React.useRef(null);

  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollLeft = 0;
  }, [items, period]);

  if (!items.length) {
    return (
      <div className="flex min-h-[320px] items-center justify-center px-6 py-10 text-[14px] text-slate-500">
        No sales trend data available for selected range.
      </div>
    );
  }

  const height = 300;
  const paddingLeft = 72;
  const paddingRight = 72;
  const plotTop = 42;
  const plotBottom = 208;
  const pointGap = period === "day" ? 108 : period === "week" ? 130 : 150;
  const width = Math.max(
    760,
    paddingLeft + paddingRight + Math.max(items.length - 1, 1) * pointGap
  );

  const values = items.map((it) => Number(it?.totalSales || 0));
  const maxValue = Math.max(...values, 1);

  const getX = (index) =>
    paddingLeft +
    (index * (width - paddingLeft - paddingRight)) /
      Math.max(items.length - 1, 1);

  const getY = (value) =>
    plotBottom -
    (Number(value || 0) / maxValue) * (plotBottom - plotTop);

  const points = items.map((item, index) => ({
    x: getX(index),
    y: getY(item?.totalSales),
    value: Number(item?.totalSales || 0),
    orders: Number(item?.totalOrders || 0),
    label: item?.bucket || "",
  }));

  const linePath = points
    .map((point, index) =>
      `${index === 0 ? "M" : "L"} ${point.x} ${point.y}`
    )
    .join(" ");

  const areaPath = `${linePath} L ${
    points[points.length - 1].x
  } ${plotBottom} L ${points[0].x} ${plotBottom} Z`;

  const gridLines = 4;

  return (
    <div className="px-5 py-5">
      <div className="rounded-[24px] border border-[#f2d7dc] bg-[linear-gradient(180deg,#fff8f9_0%,#ffffff_100%)] p-4 shadow-inner">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-[14px] font-bold text-slate-900">Revenue Trend</div>
            <div className="text-[12px] text-slate-500">
              Smooth sales overview with readable timeline
            </div>
          </div>

          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-2 rounded-full bg-[#fff1f3] px-3 py-1 text-[11px] font-semibold text-[#d86a78]">
              <span className="h-2 w-2 rounded-full bg-[#d86a78]" />
              Sales
            </span>
            <span className="rounded-full bg-[#fff6f7] px-3 py-1 text-[11px] font-semibold text-[#d86a78]">
              {period.toUpperCase()} VIEW
            </span>
          </div>
        </div>

        <div ref={scrollRef} className="overflow-x-auto pb-1">
          <svg
            viewBox={`0 0 ${width} ${height}`}
            width={width}
            height={height}
            preserveAspectRatio="xMinYMin meet"
            className="block h-[300px] max-w-none"
            style={{ width: `${width}px`, minWidth: `${width}px` }}
            role="img"
            aria-label="Warehouse revenue trend chart"
          >
            <defs>
              <linearGradient id="trendAreaFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#d86a78" stopOpacity="0.22" />
                <stop offset="55%" stopColor="#ef9aa6" stopOpacity="0.14" />
                <stop offset="100%" stopColor="#fbe3e7" stopOpacity="0.04" />
              </linearGradient>

              <linearGradient id="trendLineStroke" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#d86a78" />
                <stop offset="100%" stopColor="#ef9aa6" />
              </linearGradient>

              <filter id="lineGlow" x="-10%" y="-25%" width="120%" height="150%">
                <feGaussianBlur stdDeviation="4" result="coloredBlur" />
                <feMerge>
                  <feMergeNode in="coloredBlur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
            </defs>

            {[...Array(gridLines)].map((_, index) => {
              const y =
                plotTop +
                (index * (plotBottom - plotTop)) /
                  Math.max(gridLines - 1, 1);

              return (
                <line
                  key={index}
                  x1={paddingLeft}
                  y1={y}
                  x2={width - paddingRight}
                  y2={y}
                  stroke="#f4dbe0"
                  strokeDasharray="5 6"
                />
              );
            })}

            <path d={areaPath} fill="url(#trendAreaFill)" />
            <path
              d={linePath}
              fill="none"
              stroke="url(#trendLineStroke)"
              strokeWidth="4"
              strokeLinecap="round"
              strokeLinejoin="round"
              filter="url(#lineGlow)"
            />

            {points.map((point, index) => (
              <g key={`${point.label}-${index}`}>
                <circle
                  cx={point.x}
                  cy={point.y}
                  r="7"
                  fill="#ffffff"
                  stroke="#d86a78"
                  strokeWidth="3"
                />
                <circle cx={point.x} cy={point.y} r="3" fill="#cf5f6d" />

                <text
                  x={point.x}
                  y={Math.max(20, point.y - 17)}
                  textAnchor="middle"
                  fontSize="12"
                  fontWeight="700"
                  fill="#334155"
                >
                  {formatCurrency(point.value)}
                </text>

                <text
                  x={point.x}
                  y={Math.min(plotBottom + 22, point.y + 25)}
                  textAnchor="middle"
                  fontSize="11"
                  fontWeight="600"
                  fill="#64748b"
                >
                  {formatNumber(point.orders)} orders
                </text>

                <text
                  x={point.x}
                  y={height - 13}
                  textAnchor="middle"
                  fontSize="12"
                  fontWeight="600"
                  fill="#475569"
                >
                  {formatShortDate(point.label)}
                </text>
              </g>
            ))}
          </svg>
        </div>
      </div>
    </div>
  );
}

function ProductRow({ item, index, tone = "top" }) {
  return (
    <div className="flex items-center justify-between gap-4 rounded-[20px] border border-[#f2d7dc] bg-[linear-gradient(180deg,#fffafb_0%,#ffffff_100%)] px-4 py-4 transition hover:-translate-y-[1px] hover:shadow-[0_10px_22px_rgba(216,106,120,0.08)]">
      <div className="flex min-w-0 items-center gap-3">
        <div className="grid h-12 w-12 shrink-0 place-items-center rounded-[16px] bg-[#fff1f3] text-[#d86a78]">
          {index === 0 ? (
            <Crown size={18} />
          ) : tone === "top" ? (
            <TrendingUp size={18} />
          ) : (
            <TrendingDown size={18} />
          )}
        </div>

        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <div className="truncate text-[14px] font-bold text-slate-900">
              {item?.productName || "Unnamed product"}
            </div>
            <span className="rounded-full bg-[#fff1f3] px-2 py-0.5 text-[10px] font-semibold text-[#d86a78]">
              #{index + 1}
            </span>
          </div>
          <div className="mt-1 truncate text-[12px] text-slate-500">
            SKU: {item?.sku || "-"}
          </div>
        </div>
      </div>

      <div className="text-right">
        <div className="text-[14px] font-bold text-slate-900">
          {formatNumber(item?.totalQty)}
        </div>
        <div className="text-[12px] text-slate-500">
          {formatCurrency(item?.totalSales)}
        </div>
      </div>
    </div>
  );
}

function EmptyBlock({ text }) {
  return (
    <div className="px-5 py-10 text-center text-[14px] text-slate-500">{text}</div>
  );
}

function MiniMetric({
  icon: Icon,
  title,
  value,
  note,
  iconBg = "bg-[#fff1f3]",
  iconColor = "text-[#d86a78]",
}) {
  return (
    <div className="rounded-[20px] border border-[#f2d7dc] bg-[linear-gradient(180deg,#fffafb_0%,#ffffff_100%)] p-4 shadow-sm">
      <div className="flex items-start gap-3">
        <div
          className={`grid h-11 w-11 shrink-0 place-items-center rounded-[14px] ${iconBg} ${iconColor}`}
        >
          <Icon size={18} />
        </div>
        <div>
          <div className="text-[12px] font-semibold uppercase tracking-[0.14em] text-slate-400">
            {title}
          </div>
          <div className="mt-1 text-[24px] font-bold text-slate-900">{value}</div>
          <div className="mt-1 text-[12px] text-slate-500">{note}</div>
        </div>
      </div>
    </div>
  );
}

function QuickAction({
  icon: Icon,
  title,
  note,
  onClick,
  accent = "from-[#fffafb] to-[#ffffff]",
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`group rounded-[20px] border border-[#f2d7dc] bg-gradient-to-br ${accent} p-4 text-left transition hover:-translate-y-[1px] hover:shadow-[0_12px_26px_rgba(216,106,120,0.10)]`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="grid h-12 w-12 place-items-center rounded-[16px] bg-white text-[#d86a78] shadow-sm">
          <Icon size={18} />
        </div>
        <ChevronRight
          size={18}
          className="text-slate-400 transition group-hover:translate-x-0.5 group-hover:text-[#d86a78]"
        />
      </div>

      <div className="mt-4 text-[16px] font-bold text-slate-900">{title}</div>
      <div className="mt-1 text-[12px] leading-5 text-slate-500">{note}</div>
    </button>
  );
}

export default function WarehouseDashboardHome() {
  const navigate = useNavigate();
  const session = getAuthSession() || {};
const user = getAuthUser() || {};

const selectedWarehouseId =
  session?.selectedWarehouseId ||
  session?.warehouseAccess?.assignedWarehouseId ||
  "";

const selectedWarehouseNameFromSession =
  session?.selectedWarehouseName ||
  session?.warehouseAccess?.selectedWarehouseName ||
  session?.warehouseAccess?.assignedWarehouseName ||
  session?.warehouseAccess?.warehouses?.find(
    (w) => String(w?.id) === String(selectedWarehouseId)
  )?.name ||
  "";

  const [warehouseName, setWarehouseName] = useState("");
  const [warehouseStatus, setWarehouseStatus] = useState("");
  const [warehouseInfo, setWarehouseInfo] = useState(null);
  const [minuteTick, setMinuteTick] = useState(0);
  const [statusSaving, setStatusSaving] = useState(false);
  const [statusPopup, setStatusPopup] = useState({
    open: false,
    nextStatus: "",
  });

  const [loading, setLoading] = useState(false);
  const [pageError, setPageError] = useState("");

  const [rangeKey, setRangeKey] = useState("month");
  const [trendPeriod, setTrendPeriod] = useState("day");

  const [summary, setSummary] = useState({});
  const [trend, setTrend] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [leastProducts, setLeastProducts] = useState([]);
  const [dashboardOps, setDashboardOps] = useState({});

  const [showTopModal, setShowTopModal] = useState(false);
  const [showLeastModal, setShowLeastModal] = useState(false);

  // This filter is ONLY for /api/warehouse/order-analytics/products.
  // type is not a dropdown here because this dashboard already calls both:
  // top products + least products.
  const [productFilterMode, setProductFilterMode] = useState("period");
  const [productPeriod, setProductPeriod] = useState("month");
  const [productFromDate, setProductFromDate] = useState("");
  const [productToDate, setProductToDate] = useState("");
  const [productLimit, setProductLimit] = useState(8);

  const filters = useMemo(() => getRange(rangeKey), [rangeKey]);

  const productFilters = useMemo(() => {
    const next = {
      limit: Number(productLimit) || 8,
    };

    if (productFilterMode === "custom") {
      if (productFromDate) next.fromDate = productFromDate;
      if (productToDate) next.toDate = productToDate;
      return next;
    }

    next.period = productPeriod;
    return next;
  }, [productFilterMode, productPeriod, productFromDate, productToDate, productLimit]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setMinuteTick((tick) => tick + 1);
    }, 60 * 1000);

    return () => window.clearInterval(timer);
  }, []);

  const productFilterLabel = useMemo(() => {
    if (productFilterMode === "custom") {
      return `${productFromDate || "Start"} → ${productToDate || "Today"}`;
    }

    return `Current ${productPeriod}`;
  }, [productFilterMode, productPeriod, productFromDate, productToDate]);

  useEffect(() => {
    let mounted = true;

    async function loadWarehouseName() {
      try {
        if (!selectedWarehouseId) {
          if (mounted) {
            setWarehouseName("Not selected");
            setWarehouseStatus("");
            setWarehouseInfo(null);
          }
          return;
        }

        const res = await warehouseService.getWarehouse(selectedWarehouseId);

        const warehouse =
          res?.data?.data ||
          res?.data?.warehouse ||
          res?.warehouse ||
          res?.data ||
          res ||
          {};

        if (!mounted) return;

        setWarehouseName(
          warehouse?.name ||
            warehouse?.warehouseName ||
            warehouse?.code ||
            warehouse?.locationName ||
            selectedWarehouseNameFromSession ||
            selectedWarehouseId
        );

        setWarehouseStatus(String(warehouse?.manual_status || warehouse?.manualStatus || warehouse?.status || "").toLowerCase());
        setWarehouseInfo(warehouse || null);
      } catch {
        if (mounted) {
          setWarehouseName(
            selectedWarehouseNameFromSession || selectedWarehouseId || "Not selected"
          );
          setWarehouseStatus("");
          setWarehouseInfo(null);
        }
      }
    }

    loadWarehouseName();

    return () => {
      mounted = false;
    };
  }, [selectedWarehouseId, selectedWarehouseNameFromSession]);

  async function loadDashboard() {
    try {
      setLoading(true);
      setPageError("");

      const dashboardRes = await warehouseInventoryService.getOrderAnalyticsDashboard({
        ...filters,
        period: trendPeriod,
        productLimit: Number(productLimit) || 8,
        ...(productFilterMode === "custom"
          ? { productFromDate, productToDate }
          : { productPeriod }),
      });

      setSummary(dashboardRes?.summary || {});
      setTrend(dashboardRes?.trend?.items || []);
      setTopProducts(dashboardRes?.topProducts || []);
      setLeastProducts(dashboardRes?.leastProducts || []);
      setDashboardOps(dashboardRes?.operations || {});
    } catch (err) {
      setPageError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadDashboard();
  }, [rangeKey, trendPeriod, productFilters]);

  const canToggleWarehouseStatus =
    String(user?.role || "").toLowerCase() === "super_admin" ||
    hasPermission(["admin.warehouses.update", "warehouses.update"]);

  const warehouseRuntime = useMemo(
    () =>
      getWarehouseRuntimeState({
        ...(warehouseInfo || {}),
        status: warehouseStatus,
      }),
    [warehouseInfo, warehouseStatus, minuteTick]
  );

  const isWarehouseActive = warehouseRuntime.isManualActive;

  const nextWarehouseStatus = isWarehouseActive ? "inactive" : "active";

  async function confirmWarehouseStatusToggle() {
    if (statusSaving) return;
    if (!selectedWarehouseId || !statusPopup.nextStatus) return;

    try {
      setStatusSaving(true);

      const res = await warehouseService.setWarehouseStatus(
        selectedWarehouseId,
        statusPopup.nextStatus
      );

      const updatedWarehouse =
        res?.data?.data ||
        res?.data?.warehouse ||
        res?.warehouse ||
        res?.data ||
        res ||
        {};

      setWarehouseStatus(
        String(
          updatedWarehouse?.manual_status ||
            updatedWarehouse?.manualStatus ||
            updatedWarehouse?.status ||
            statusPopup.nextStatus ||
            ""
        ).toLowerCase()
      );
      setWarehouseInfo(updatedWarehouse || null);
      setStatusPopup({ open: false, nextStatus: "" });

      await loadDashboard();
    } catch (err) {
      alert(err?.message || "Failed to update warehouse status");
    } finally {
      setStatusSaving(false);
    }
  }

  const stockSummary = useMemo(() => ({
    totalQty: Number(dashboardOps?.totalStockQty || 0),
    lowStockCount: Number(dashboardOps?.lowStockCount || 0),
    expiringCount: Number(dashboardOps?.expiringCount || 0),
    expiredCount: Number(dashboardOps?.expiredCount || 0),
  }), [dashboardOps]);

  const customerOrderSummary = useMemo(() => ({
    processing: Number(dashboardOps?.processing || 0),
    packed: Number(dashboardOps?.packed || 0),
    ready: Number(dashboardOps?.ready || 0),
    dispatched: Number(dashboardOps?.dispatched || 0),
  }), [dashboardOps]);

  const topInsightText = useMemo(() => {
    if (!topProducts.length) return "No top-selling product available right now.";
    const top = topProducts[0];
    return `${top?.productName || "Top product"} generated ${formatCurrency(
      top?.totalSales
    )} revenue with ${formatNumber(top?.totalQty)} units sold.`;
  }, [topProducts]);

  const leastInsightText = useMemo(() => {
    if (!leastProducts.length) return "No low-performing product insight available.";
    const item = leastProducts[0];
    return `${item?.productName || "Lowest product"} moved only ${formatNumber(
      item?.totalQty
    )} units in this selected range.`;
  }, [leastProducts]);

  return (
    <>
      <ProductModal
        open={showTopModal}
        title="Top Sold Products"
        subtitle="Best performing products based on sold quantity and revenue."
        items={topProducts}
        tone="top"
        onClose={() => setShowTopModal(false)}
      />

      <ProductModal
        open={showLeastModal}
        title="Least Sold Products"
        subtitle="Products that need attention or better movement."
        items={leastProducts}
        tone="least"
        onClose={() => setShowLeastModal(false)}
      />

      <ConfirmModal
        open={statusPopup.open}
        icon={statusPopup.nextStatus === "active" ? "success" : "ban"}
        variant="confirm"
        title={`${
          statusPopup.nextStatus === "active" ? "Activate" : "Deactivate"
        } ${warehouseName || "warehouse"}?`}
        description={
          statusPopup.nextStatus === "active"
            ? "This warehouse will become serviceable again for operations."
            : "This warehouse will be marked inactive and should not be used for serviceability until activated again."
        }
        onClose={() =>
          statusSaving ? null : setStatusPopup({ open: false, nextStatus: "" })
        }
        onConfirm={confirmWarehouseStatusToggle}
        cancelText="NO"
        confirmText={statusSaving ? "SAVING..." : "YES"}
      />

      <div className="space-y-6">
        <section className="relative isolate overflow-hidden rounded-[30px] border border-[#f5d9df] bg-[#fffafb] shadow-[0_18px_55px_rgba(64,32,40,0.06)]">
          <div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(circle_at_57%_25%,rgba(251,190,207,0.40),transparent_22%),radial-gradient(circle_at_14%_93%,rgba(255,205,220,0.58),transparent_28%),linear-gradient(120deg,#fffafb_0%,#fff7fa_45%,#ffffff_100%)]" />
          <div className="pointer-events-none absolute -bottom-28 -left-16 -z-10 h-52 w-[58%] rotate-[8deg] rounded-[50%] bg-[#ffdbe7]/65" />
          <div className="pointer-events-none absolute bottom-[-86px] left-[18%] -z-10 h-44 w-[54%] -rotate-[8deg] rounded-[50%] bg-white/80" />
          <div className="pointer-events-none absolute left-[39%] top-[12%] -z-10 h-8 w-8 rounded-full bg-[#ffdbe8]/70" />

          <div className="grid gap-5 p-5 sm:p-7 lg:p-8 xl:grid-cols-[minmax(0,1fr)_400px] xl:gap-7">
            <div className="relative min-h-[500px] xl:min-h-[446px]">
              <div className="relative z-20 max-w-[760px]">
                <div className="inline-flex items-center gap-2 rounded-full bg-[#ffeaf0] px-4 py-2 text-[12px] font-bold text-[#fb4773]">
                  <ChartNoAxesColumnIncreasing size={15} strokeWidth={2.2} />
                  Futuristic Warehouse Analytics
                </div>

                <h1 className="mt-3 max-w-[680px] text-[34px] font-black leading-[1.02] tracking-[-0.04em] text-[#090d23] sm:text-[42px]">
                  Sales, stock and <span className="text-[#d86a78]">operations</span>
                </h1>

                <p className="mt-2 max-w-[660px] text-[13px] leading-[1.55] text-[#576785] sm:text-[14px]">
                  Track revenue growth, product movement, stock pressure and dispatch readiness
                  from one warehouse-focused dashboard.
                </p>

                <div className="mt-4 grid grid-cols-2 gap-2.5 lg:grid-cols-4">
                  <InsightChip
                    icon={Warehouse}
                    label="Active Warehouse"
                    value={warehouseName || "Not selected"}
                    tone="rose"
                  />
                  <InsightChip
                    icon={Power}
                    label="Warehouse Status"
                    value={warehouseRuntime.label}
                    tone="mint"
                  />
                  <InsightChip
                    icon={ShoppingCart}
                    label="Orders"
                    value={formatNumber(summary?.totalOrders)}
                    tone="blue"
                  />
                  <InsightChip
                    icon={IndianRupee}
                    label="Sales"
                    value={formatCurrency(summary?.totalSales)}
                    tone="amber"
                  />
                </div>
              </div>

              <div className="pointer-events-none absolute bottom-[-24px] left-0 right-0 z-10 flex items-end justify-center sm:bottom-[-28px] sm:justify-end">
                <div className="absolute bottom-[80px] left-1 hidden -rotate-6 text-center font-['Segoe_Print'] text-[17px] italic leading-7 text-[#f44571] lg:block">
                  “Smarter Warehouses,<br />Stronger Businesses”
                  <div className="mx-auto mt-1 h-[2px] w-28 -rotate-6 rounded-full bg-[#f44571]" />
                </div>
                <div className="absolute bottom-[72px] left-[31%] hidden items-center gap-2 rounded-[11px] bg-white/95 px-3 py-2 text-[11px] font-extrabold text-slate-800 shadow-[0_8px_24px_rgba(44,31,35,0.12)] sm:flex">
                  Move<br />Faster
                  <TrendingUp size={24} className="text-[#fa4772]" strokeWidth={2.4} />
                </div>
                <MapPin className="absolute bottom-[122px] right-[13%] hidden text-[#f26b7f] drop-shadow-md sm:block" size={30} fill="#ff91a0" strokeWidth={1.6} />
                <img
                  src={warehouseDashboardHero}
                  alt="Warehouse, delivery van and forklift"
                  className="h-auto w-full max-w-[610px] object-contain drop-shadow-[0_16px_18px_rgba(92,50,58,0.13)] sm:w-[70%]"
                />
              </div>
            </div>

            <aside className="w-full self-stretch rounded-[24px] border border-[#f8e6ea] bg-white/95 p-4 shadow-[0_18px_40px_rgba(75,38,47,0.08)] backdrop-blur-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-[15px] font-extrabold text-[#0d1429]">
                  <span className="grid h-8 w-8 place-items-center rounded-full bg-[#fff0f4] text-[#fa4772]">
                    <Sparkles size={17} />
                  </span>
                  Dashboard Controls
                </div>
                <div className="inline-flex items-center gap-1.5 rounded-full bg-[#eafaf4] px-3 py-1.5 text-[10px] font-bold text-[#0ba575]">
                  <span className="h-1.5 w-1.5 rounded-full bg-[#0bb47e]" /> Live
                </div>
              </div>

              <div className="mt-3 grid grid-cols-2 gap-2">
                <label className="relative">
                  <CalendarDays className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#71819c]" size={15} />
                  <select
                    aria-label="Dashboard date range"
                    value={rangeKey}
                    onChange={(e) => setRangeKey(e.target.value)}
                    className="h-[44px] w-full appearance-auto rounded-[13px] border border-[#f1dfe3] bg-white pl-9 pr-2 text-[12px] text-slate-800 outline-none transition focus:border-[#f26c89] focus:ring-2 focus:ring-[#ffe6ec]"
                  >
                    <option value="month">Current month</option>
                    <option value="7d">Last 7 days</option>
                    <option value="30d">Last 30 days</option>
                    <option value="90d">Last 90 days</option>
                    <option value="all">All time</option>
                  </select>
                </label>

                <label className="relative">
                  <TrendingUp className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#71819c]" size={15} />
                  <select
                    aria-label="Dashboard trend period"
                    value={trendPeriod}
                    onChange={(e) => setTrendPeriod(e.target.value)}
                    className="h-[44px] w-full appearance-auto rounded-[13px] border border-[#f1dfe3] bg-white pl-9 pr-2 text-[12px] text-slate-800 outline-none transition focus:border-[#f26c89] focus:ring-2 focus:ring-[#ffe6ec]"
                  >
                    <option value="day">Day trend</option>
                    <option value="week">Week trend</option>
                    <option value="month">Month trend</option>
                  </select>
                </label>
              </div>

              <div className="mt-3 rounded-[17px] border border-[#f3dfe4] bg-[#fffafb] p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex min-w-0 gap-2">
                    <Filter size={19} className="mt-0.5 shrink-0 text-[#fa4772]" />
                    <div>
                      <div className="text-[12px] font-extrabold text-slate-900">Product Analytics Filter</div>
                      <div className="mt-0.5 text-[10px] leading-4 text-slate-500">
                        Applies to Top Sold and Least Sold products
                      </div>
                    </div>
                  </div>
                  <span className="shrink-0 rounded-full bg-[#fff0f4] px-2.5 py-1 text-[9px] font-bold capitalize text-[#ef5d79]">
                    {productFilterLabel}
                  </span>
                </div>

                <div className="mt-3 grid grid-cols-2 gap-2">
                  <select
                    value={productFilterMode}
                    onChange={(e) => setProductFilterMode(e.target.value)}
                    className="h-[40px] min-w-0 rounded-[12px] border border-[#f1dfe3] bg-white px-3 text-[11px] outline-none focus:border-[#f26c89]"
                  >
                    <option value="period">Current period</option>
                    <option value="custom">Custom date</option>
                  </select>

                  {productFilterMode === "period" ? (
                    <select
                      value={productPeriod}
                      onChange={(e) => setProductPeriod(e.target.value)}
                      className="h-[40px] min-w-0 rounded-[12px] border border-[#f1dfe3] bg-white px-3 text-[11px] outline-none focus:border-[#f26c89]"
                    >
                      <option value="day">Today</option>
                      <option value="week">This week</option>
                      <option value="month">This month</option>
                    </select>
                  ) : (
                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={productLimit}
                      onChange={(e) => setProductLimit(e.target.value)}
                      className="h-[40px] min-w-0 rounded-[12px] border border-[#f1dfe3] bg-white px-3 text-[11px] outline-none focus:border-[#f26c89]"
                      placeholder="Limit"
                    />
                  )}
                </div>

                {productFilterMode === "custom" ? (
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <input
                      type="date"
                      value={productFromDate}
                      onChange={(e) => setProductFromDate(e.target.value)}
                      className="h-[40px] min-w-0 rounded-[12px] border border-[#f1dfe3] bg-white px-2 text-[10px] outline-none focus:border-[#f26c89]"
                    />
                    <input
                      type="date"
                      value={productToDate}
                      onChange={(e) => setProductToDate(e.target.value)}
                      className="h-[40px] min-w-0 rounded-[12px] border border-[#f1dfe3] bg-white px-2 text-[10px] outline-none focus:border-[#f26c89]"
                    />
                  </div>
                ) : (
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <input
                      type="number"
                      min="1"
                      max="100"
                      value={productLimit}
                      onChange={(e) => setProductLimit(e.target.value)}
                      className="h-[40px] min-w-0 rounded-[12px] border border-[#f1dfe3] bg-white px-3 text-[11px] outline-none focus:border-[#f26c89]"
                      placeholder="Limit"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setProductFilterMode("period");
                        setProductPeriod("month");
                        setProductFromDate("");
                        setProductToDate("");
                        setProductLimit(8);
                      }}
                      className="h-[40px] rounded-[12px] border border-[#f5dce2] bg-[#fff4f7] text-[11px] font-bold text-[#ed5875] transition hover:bg-[#ffeaf0]"
                    >
                      <RefreshCcw size={13} className="mr-1.5 inline" /> Reset
                    </button>
                  </div>
                )}

                {productFilterMode === "custom" ? (
                  <button
                    type="button"
                    onClick={() => {
                      setProductFilterMode("period");
                      setProductPeriod("month");
                      setProductFromDate("");
                      setProductToDate("");
                      setProductLimit(8);
                    }}
                    className="mt-2 h-[36px] w-full rounded-[12px] border border-[#f5dce2] bg-[#fff4f7] text-[11px] font-bold text-[#ed5875] transition hover:bg-[#ffeaf0]"
                  >
                    Reset Product Filter
                  </button>
                ) : null}
              </div>

              <button
                type="button"
                onClick={loadDashboard}
                className="mt-2.5 inline-flex h-[44px] w-full items-center justify-center gap-2 rounded-[13px] bg-[#d86a78] px-4 text-[13px] font-bold text-white shadow-[0_9px_20px_rgba(216,106,120,0.18)] transition hover:-translate-y-0.5 hover:bg-[#c95b69] hover:shadow-[0_12px_24px_rgba(216,106,120,0.24)]"
              >
                <RefreshCcw size={17} className={loading ? "animate-spin" : ""} />
                {loading ? "Refreshing..." : "Refresh Dashboard"}
              </button>

              {canToggleWarehouseStatus && selectedWarehouseId ? (
                <button
                  type="button"
                  disabled={statusSaving || !warehouseStatus}
                  onClick={() =>
                    setStatusPopup({
                      open: true,
                      nextStatus: nextWarehouseStatus,
                    })
                  }
                  className={
                    "mt-2.5 inline-flex h-[44px] w-full items-center justify-center gap-2 rounded-[13px] px-4 text-[13px] font-bold text-white shadow-[0_9px_20px_rgba(8,158,111,0.16)] transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60 " +
                    (isWarehouseActive
                      ? "bg-slate-900 hover:bg-slate-800"
                      : "bg-[linear-gradient(90deg,#08a477_0%,#08b688_100%)]")
                  }
                >
                  <Power size={17} />
                  {isWarehouseActive ? "Turn Warehouse Off" : "Turn Warehouse On"}
                </button>
              ) : null}

              <div className="mt-2.5 grid grid-cols-2 gap-2">
                <div className="flex items-center gap-2 rounded-[14px] bg-[linear-gradient(135deg,#fff4f7_0%,#fffafb_100%)] p-3">
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-white text-[#fa4772] shadow-sm">
                    <ChartNoAxesColumnIncreasing size={15} />
                  </span>
                  <div className="min-w-0">
                    <div className="truncate text-[9px] font-bold uppercase tracking-[0.12em] text-slate-400">Avg Order</div>
                    <div className="mt-0.5 truncate text-[16px] font-black text-slate-900">{formatCurrency(summary?.averageOrderValue)}</div>
                  </div>
                </div>

                <div className="flex items-center gap-2 rounded-[14px] bg-[linear-gradient(135deg,#fff4f7_0%,#fffafb_100%)] p-3">
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-white text-[#fa4772] shadow-sm">
                    <ShoppingCart size={15} />
                  </span>
                  <div className="min-w-0">
                    <div className="truncate text-[9px] font-bold uppercase tracking-[0.12em] text-slate-400">Active Orders</div>
                    <div className="mt-0.5 truncate text-[16px] font-black text-slate-900">{formatNumber(summary?.activeOrders)}</div>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </section>

        {pageError ? (
          <div className="rounded-[18px] border border-[#f3d8de] bg-[#fff8fa] px-4 py-3 text-[14px] font-medium text-[#d9465f]">
            {pageError}
          </div>
        ) : null}

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
          <StatCard
            icon={IndianRupee}
            title="Total Sales"
            value={formatCurrency(summary?.totalSales)}
            note="Total paid sales in selected range"
            chip="Revenue"
            glow="from-[#fff7f8] via-[#ffffff] to-[#fff1f3]"
            iconBg="bg-[#fff1f3]"
            iconText="text-[#d86a78]"
          />
          <StatCard
            icon={ShoppingCart}
            title="Total Orders"
            value={formatNumber(summary?.totalOrders)}
            note="Customer orders received"
            chip="Orders"
            glow="from-[#fffafb] via-[#ffffff] to-[#fff3f5]"
            iconBg="bg-[#fff1f3]"
            iconText="text-[#d86a78]"
          />
          <StatCard
            icon={PackageCheck}
            title="Delivered"
            value={formatNumber(summary?.deliveredOrders)}
            note="Completed successful deliveries"
            chip="Completed"
            glow="from-[#fff8f9] via-[#ffffff] to-[#fff2f4]"
            iconBg="bg-[#fff1f3]"
            iconText="text-[#d86a78]"
          />
          <StatCard
            icon={Boxes}
            title="Stock Qty"
            value={formatNumber(stockSummary.totalQty)}
            note="Current available stock units"
            chip="Inventory"
            glow="from-[#fffafb] via-[#ffffff] to-[#fff4f6]"
            iconBg="bg-[#fff1f3]"
            iconText="text-[#d86a78]"
          />
        </div>

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1.6fr_1fr]">
          <Card>
            <SectionHeader
              title="Sales Trend Analytics"
              subtitle="Readable sales movement with a modern line + area chart"
              right={
                <div className="rounded-full bg-[#fff1f3] px-3 py-1 text-[11px] font-semibold text-[#d86a78]">
                  {trendPeriod.toUpperCase()} VIEW
                </div>
              }
            />
            <TrendChart items={trend} period={trendPeriod} />
          </Card>

          <Card>
            <SectionHeader
              title="Insights"
              subtitle="Fast business summary from current analytics"
            />
            <div className="space-y-4 px-5 py-5">
              <div className="rounded-[20px] border border-[#f2d7dc] bg-[linear-gradient(180deg,#fffafb_0%,#ffffff_100%)] p-4">
                <div className="flex items-center gap-2 text-[13px] font-bold text-slate-900">
                  <TrendingUp size={16} className="text-[#d86a78]" />
                  Best Performance
                </div>
                <div className="mt-2 text-[13px] leading-6 text-slate-600">
                  {topInsightText}
                </div>
              </div>

              <div className="rounded-[20px] border border-[#f2d7dc] bg-[linear-gradient(180deg,#fffafb_0%,#ffffff_100%)] p-4">
                <div className="flex items-center gap-2 text-[13px] font-bold text-slate-900">
                  <TrendingDown size={16} className="text-[#d86a78]" />
                  Improvement Area
                </div>
                <div className="mt-2 text-[13px] leading-6 text-slate-600">
                  {leastInsightText}
                </div>
              </div>

              <div className="rounded-[20px] border border-[#f2d7dc] bg-[linear-gradient(180deg,#fffafb_0%,#ffffff_100%)] p-4">
                <div className="flex items-center gap-2 text-[13px] font-bold text-slate-900">
                  <ShieldAlert size={16} className="text-[#d86a78]" />
                  Inventory Risk
                </div>
                <div className="mt-2 text-[13px] leading-6 text-slate-600">
                  {stockSummary.lowStockCount > 0
                    ? `${formatNumber(
                        stockSummary.lowStockCount
                      )} items are low on stock. Replenishment planning should be prioritized.`
                    : "No immediate low-stock pressure found in current inventory snapshot."}
                </div>
              </div>
            </div>
          </Card>
        </div>

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
          <Card>
            <SectionHeader
              title="Top Sold Products"
              subtitle={`Highest performing products by qty and revenue • ${productFilterLabel}`}
              right={
                <button
                  type="button"
                  onClick={() => setShowTopModal(true)}
                  className="inline-flex items-center gap-2 rounded-full bg-[#fff1f3] px-3 py-1.5 text-[12px] font-semibold text-[#d86a78] transition hover:bg-[#ffe6ea]"
                >
                  <Eye size={14} />
                  View all
                </button>
              }
            />
            <div className="space-y-3 px-5 py-5">
              {topProducts.length ? (
                topProducts
                  .slice(0, 3)
                  .map((item, idx) => (
                    <ProductRow
                      key={`${item?.productId || item?.sku || idx}`}
                      item={item}
                      index={idx}
                      tone="top"
                    />
                  ))
              ) : (
                <EmptyBlock text="No top-selling product data found." />
              )}
            </div>
          </Card>

          <Card>
            <SectionHeader
              title="Least Sold Products"
              subtitle={`Products needing more movement or review • ${productFilterLabel}`}
              right={
                <button
                  type="button"
                  onClick={() => setShowLeastModal(true)}
                  className="inline-flex items-center gap-2 rounded-full bg-[#fff1f3] px-3 py-1.5 text-[12px] font-semibold text-[#d86a78] transition hover:bg-[#ffe6ea]"
                >
                  <Eye size={14} />
                  View all
                </button>
              }
            />
            <div className="space-y-3 px-5 py-5">
              {leastProducts.length ? (
                leastProducts
                  .slice(0, 3)
                  .map((item, idx) => (
                    <ProductRow
                      key={`${item?.productId || item?.sku || idx}`}
                      item={item}
                      index={idx}
                      tone="least"
                    />
                  ))
              ) : (
                <EmptyBlock text="No least-selling product data found." />
              )}
            </div>
          </Card>
        </div>

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-[1.15fr_1fr]">
          <Card>
            <SectionHeader
              title="Operations Health"
              subtitle="Monitor stock, receiving and dispatch workload"
            />
            <div className="grid grid-cols-1 gap-4 px-5 py-5 sm:grid-cols-2 xl:grid-cols-3">
              <MiniMetric
                icon={AlertTriangle}
                title="Low Stock"
                value={formatNumber(stockSummary.lowStockCount)}
                note="Items below threshold"
                iconBg="bg-[#fff1f3]"
                iconColor="text-[#d86a78]"
              />
              <MiniMetric
                icon={Clock3}
                title="Expiring"
                value={formatNumber(stockSummary.expiringCount)}
                note="Items with expiring batches"
                iconBg="bg-[#fff1f3]"
                iconColor="text-[#d86a78]"
              />
              <MiniMetric
                icon={ShieldAlert}
                title="Expired"
                value={formatNumber(stockSummary.expiredCount)}
                note="Items with expired quantity"
                iconBg="bg-[#fff1f3]"
                iconColor="text-[#d86a78]"
              />
              <MiniMetric
                icon={Package}
                title="Pending Inbound"
                value={formatNumber(dashboardOps?.pendingInbound)}
                note="Receipts waiting to receive"
                iconBg="bg-[#fff1f3]"
                iconColor="text-[#d86a78]"
              />
              <MiniMetric
                icon={Truck}
                title="Ready Dispatch"
                value={formatNumber(customerOrderSummary.ready)}
                note="Ready for dispatch"
                iconBg="bg-[#fff1f3]"
                iconColor="text-[#d86a78]"
              />
              <MiniMetric
                icon={Activity}
                title="Packed Orders"
                value={formatNumber(customerOrderSummary.packed)}
                note="Packed and queued"
                iconBg="bg-[#fff1f3]"
                iconColor="text-[#d86a78]"
              />
            </div>
          </Card>

          <Card>
            <SectionHeader
              title="Quick Actions"
              subtitle="Open detail modules directly"
            />
            <div className="grid grid-cols-1 gap-4 px-5 py-5">
              <QuickAction
                icon={ClipboardList}
                title="Customer Orders"
                note="Open order list, filters, dispatch workflow and status details."
                onClick={() => navigate("/admin/dashboard/customer-orders")}
                accent="from-[#fffafb] to-[#ffffff]"
              />
              <QuickAction
                icon={Boxes}
                title="Inventory"
                note="Check stock, expired items, low stock alerts and inbound status."
                onClick={() => navigate("/admin/dashboard/inventory")}
                accent="from-[#fffafb] to-[#ffffff]"
              />
              <QuickAction
                icon={Warehouse}
                title="Products"
                note="Review assigned products, top sellers and low movement items."
                onClick={() => navigate("/admin/dashboard/products")}
                accent="from-[#fffafb] to-[#ffffff]"
              />
              <QuickAction
                icon={Activity}
                title="Employees"
                note="View warehouse team access, activity and operational staff details."
                onClick={() => navigate("/admin/dashboard/employees")}
                accent="from-[#fffafb] to-[#ffffff]"
              />
            </div>
          </Card>
        </div>
      </div>
    </>
  );
}
